import React, { cloneElement, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import macosFolderBlack from '@/assets/agent-user-detail/macos-folder-black@3x.webp';
import { RadioButton } from '@/components/common/radio-button';
import { KnowledgeFolderData } from '@/services/knowledge-list-folders';
import { KnowledgeEmptyIcon } from '../agent-dashboard-user-detail/assets';

interface KnowledgeModalAddProps {
  /** 知识库目录列表 */
  knowledgeFolders?: KnowledgeFolderData[];
  trigger?: React.ReactElement;
  open?: boolean;
  loading?: boolean;
  selectedIds?: number[];
  allowSelectEmpty?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (ids: number[]) => Promise<void>;
}

/** 添加知识库弹窗 */
export const KnowledgeModalAdd: React.FC<KnowledgeModalAddProps> = ({
  knowledgeFolders,
  trigger,
  open: externalOpen,
  selectedIds: externalSelectedIds,
  allowSelectEmpty = false,
  loading,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>(externalSelectedIds || []);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  useEffect(() => {
    if (!currentOpen) {
      setSelectedIds(externalSelectedIds || []);
    }
  }, [externalSelectedIds, currentOpen]);

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (open) {
      setSelectedIds(selectedIds || []);
    }
  };

  const handleSave = async () => {
    await onSave?.(selectedIds);
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 处理选择知识库
  const handleKnowledgeSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  // 计算全选状态
  const selectAll = useMemo(() => {
    if (!knowledgeFolders?.length) return false;
    return selectedIds.length === knowledgeFolders.length;
  }, [selectedIds, knowledgeFolders]);

  // 处理全选/取消全选
  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(knowledgeFolders?.map(item => item.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[556px] max-h-[665px] min-h-[665px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('agent_dashboard.user_detail.knowledge.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 px-6 select-none overflow-y-auto pb-5'>
          {knowledgeFolders?.length ? <div className='grid grid-cols-4 gap-3'>
            {knowledgeFolders.map((material, index) => (
              <div key={material.id} className="w-[118px] h-full relative">
                <div
                  className="group relative w-full rounded-[12px] transition-all cursor-pointer select-none"
                  onClick={() => handleKnowledgeSelect(material.id)}
                >
                  <div className='overflow-hidden flex-shrink-0'>
                    <Image src={macosFolderBlack} className="size-full object-cover" />
                  </div>
                  {!selectedIds.includes(material.id) && (
                    <div className="absolute inset-0 bg-[rgba(255,255,255,0.4)]"></div>
                  )}
                  {selectedIds.includes(material.id) && (
                    <div
                      className={cn(
                        "absolute top-6 right-3 flex items-center justify-center size-5 rounded-full border border-[#667085] transition-all",
                        "bg-[rgba(0,0,0,0.4)] border-none",
                      )}
                    >
                      <Check className="size-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="w-full mt-2">
                  <div className="w-full text-center">
                    <TruncatedText text={material.folderName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
                  </div>
                  <div className="w-full text-center mt-1">
                    <TruncatedText text={`${material.itemCount || 0} ${t('agent_dashboard.user_detail.knowledge.add_modal.items')}`} className='max-w-[90%] text-xs text-[rgba(102,112,133,1)]' />
                  </div>
                </div>
              </div>
            ))}
          </div> : <div className='w-full h-full min-h-[450px] relative flex flex-col justify-center items-center'>
            <KnowledgeEmptyIcon className='text-[rgba(102,112,133,1)]' />
            <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.knowledge.detail_modal.empty_tips2")}</span>
          </div>}
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[76px] flex justify-between px-6 pt-3 pb-5'>
          <div className='flex items-center gap-2'>
            <RadioButton
              label={t('agent_dashboard.user_detail.voice.add_modal.all_selecting')}
              checked={selectAll}
              onChange={handleToggleSelectAll}
              disabled={knowledgeFolders?.length === 0}
            />
            <div className='text-xs text-[rgba(71,84,103,1)]'>
              {t('agent_dashboard.user_detail.knowledge.add_modal.selected')} {selectedIds.length}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.knowledge.add_modal.cancel')}
              className='w-[110px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.knowledge.add_modal.save')}
              className='w-[110px] h-11'
              onClick={handleSave}
              disabled={loading || (!allowSelectEmpty && selectedIds?.length === 0)}
              loading={loading}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
